import { useCallback, useEffect, useMemo, useState } from "react";
import useCheckoutStore from "src/store/checkout.store";
import { useCouponStore } from "src/store/useCouponStore";
import { calculateDiscountAmount, calculateDiscountedAmount } from "..";
import { validateCoupon } from "../utils/couponCalculator";
const useCouponService = (config)=>{
    const { userCoupons  } = config;
    const { applyCoupon: applyCouponFromStore , removeCoupon: removeCouponFromStore , appliedCoupons , clearAllCoupons  } = useCouponStore((state)=>({
            applyCoupon: state.applyCoupon,
            removeCoupon: state.removeCoupon,
            appliedCoupons: state.appliedCoupons,
            clearAllCoupons: state.clearAllCoupons
        }));
    const { orderItems: currentOrderItems , applyProductCoupon , removeProductCoupon  } = useCheckoutStore((state)=>({
            orderItems: state.orderItems,
            applyProductCoupon: state.applyProductCoupon,
            removeProductCoupon: state.removeProductCoupon
        }));
    // 내부 상태 관리 - 에러에 타임스탬프를 포함하여 같은 메시지도 새로운 에러로 인식
    const [errors, setErrors] = useState({});
    const findPrice = (item)=>{
        return Boolean(item === null || item === void 0 ? void 0 : item.pod) ? item.pod.options.price : item.price;
    };
    // 에러 클리어 함수
    const clearError = useCallback((itemId)=>{
        setErrors((prev)=>{
            const { [itemId]: _ , ...rest } = prev;
            return rest;
        });
    }, []);
    // 쿠폰 적용
    const applyCoupon = useCallback((itemId, coupon)=>{
        const item = currentOrderItems.find((i)=>i.id === itemId);
        if (!item) return false;
        const validation = validateCoupon(item, coupon);
        if (!validation.isValid) {
            setErrors((prev)=>({
                    ...prev,
                    [itemId]: {
                        message: validation.reason || "",
                        timestamp: Date.now()
                    }
                }));
            return false;
        }
        // 유효성 검증 통과 시 에러 먼저 클리어
        clearError(itemId);
        const discountedPrice = calculateDiscountedAmount({
            price: item.total,
            discount: calculateDiscountAmount({
                discountType: coupon.discountType,
                discountValue: coupon.discountValue,
                discountMaximumPrice: coupon.discountMaximumPrice,
                price: item.total
            })
        });
        // 이미 다른 상품에 적용된 쿠폰인지 확인하고 제거
        const previousItemId = Object.keys(appliedCoupons).find((id)=>{
            var _appliedCoupons_id;
            return ((_appliedCoupons_id = appliedCoupons[id]) === null || _appliedCoupons_id === void 0 ? void 0 : _appliedCoupons_id.couponId) === coupon.id && id !== itemId;
        });
        if (previousItemId) {
            // checkout store에서 이전 상품의 쿠폰 제거
            removeProductCoupon(previousItemId);
            // 이전 상품의 에러도 클리어
            clearError(previousItemId);
        }
        // 쿠폰 스토어 업데이트
        applyCouponFromStore(itemId, {
            couponId: coupon.id,
            discountAmount: parseInt(calculateDiscountedAmount({
                price: item.total,
                discount: discountedPrice
            }))
        });
        applyProductCoupon(itemId, coupon.id, parseInt(discountedPrice));
        return true;
    }, [
        currentOrderItems,
        applyProductCoupon,
        appliedCoupons,
        removeProductCoupon,
        clearError
    ]);
    const removeCoupon = useCallback((itemId)=>{
        const appliedCoupon = appliedCoupons[itemId];
        if (!appliedCoupon) return false;
        removeCouponFromStore(itemId);
        removeProductCoupon(itemId);
        clearError(itemId);
        return true;
    }, [
        appliedCoupons,
        removeProductCoupon,
        clearError
    ]);
    // 조회 함수들
    const checkCouponFunctions = useMemo(()=>{
        return {
            getAvailableCoupons: ()=>{
                return userCoupons === null || userCoupons === void 0 ? void 0 : userCoupons.filter((coupon)=>!coupon.isApplied);
            },
            hasCoupon: (itemId)=>appliedCoupons[itemId] !== undefined,
            getAppliedCoupon: (itemId)=>appliedCoupons[itemId],
            getError: (itemId)=>{
                var _errors_itemId;
                return (_errors_itemId = errors[itemId]) === null || _errors_itemId === void 0 ? void 0 : _errors_itemId.message;
            },
            hasAnyCoupon: ()=>Object.keys(appliedCoupons).length > 0,
            canApplyCoupon: (itemId, coupon)=>{
                const item = currentOrderItems === null || currentOrderItems === void 0 ? void 0 : currentOrderItems.find((i)=>i.id === itemId);
                if (!item) return false;
                return validateCoupon(item, coupon).isValid;
            }
        };
    }, [
        userCoupons,
        appliedCoupons,
        errors,
        currentOrderItems
    ]);
    useEffect(()=>{
        clearAllCoupons();
    }, []);
    return {
        applyCoupon,
        removeCoupon,
        ...checkCouponFunctions,
        errors
    };
};
export default useCouponService;
